import { useEffect, useMemo, useState } from 'react';

import { cn } from '~/utils/cn';
import {
  createCalendarSubscriptionRequest,
  downloadCalendarIcs,
  type CalendarSubscription,
} from '~/services/http/requests/calendar';

import Backdrop from '../Backdrop/view';
import Button from '../../atoms/Button/view';
import FilterChips from '../FilterChips/view';
import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';
import type { propsIconsNames } from '../../atoms/Icon/interfaces';

export type CalendarLinkScope = 'contract' | 'all';

export interface CalendarLinkModalProps {
  open: boolean;
  onClose: () => void;
  contractId: string | null;
  contractLabel?: string;
}

type Feedback = { tone: 'success' | 'error'; message: string } | null;

export default function CalendarLinkModal({
  open,
  onClose,
  contractId,
  contractLabel,
}: CalendarLinkModalProps) {
  const [scope, setScope] = useState<CalendarLinkScope>(
    contractId ? 'contract' : 'all',
  );
  const [hideAmounts, setHideAmounts] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [subscription, setSubscription] = useState<CalendarSubscription | null>(
    null,
  );

  const targetContractId = scope === 'contract' ? contractId : null;

  const scopeItems = useMemo(() => {
    const items: { key: CalendarLinkScope; label: string }[] = [];
    if (contractId) items.push({ key: 'contract', label: 'Só este contrato' });
    items.push({ key: 'all', label: 'Todos os contratos' });
    return items;
  }, [contractId]);

  useEffect(() => {
    if (!open) return;
    setScope(contractId ? 'contract' : 'all');
    setHideAmounts(false);
    setBusy(null);
    setFeedback(null);
    setSubscription(null);
  }, [open, contractId]);

  useEffect(() => {
    setSubscription(null);
    setFeedback(null);
  }, [scope, hideAmounts]);

  const ensureSubscription = async (): Promise<CalendarSubscription> => {
    if (subscription) return subscription;
    const { data } = await createCalendarSubscriptionRequest({
      scope,
      contractId: targetContractId ?? undefined,
      hideAmounts,
    });
    setSubscription(data);
    return data;
  };

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    setFeedback(null);
    try {
      await action();
    } catch {
      setFeedback({
        tone: 'error',
        message:
          'Não foi possível gerar o link do calendário agora. Tente novamente.',
      });
    } finally {
      setBusy(null);
    }
  };

  const openInNewTab = (key: string, pick: (s: CalendarSubscription) => string) =>
    run(key, async () => {
      const data = await ensureSubscription();
      window.open(pick(data), '_blank', 'noopener,noreferrer');
      setFeedback({
        tone: 'success',
        message:
          'Confirme a assinatura na aba que abriu. Depois disso, as parcelas se atualizam sozinhas.',
      });
    });

  const handleCopy = () =>
    run('copy', async () => {
      const data = await ensureSubscription();
      await navigator.clipboard.writeText(data.urls.ics);
      setFeedback({
        tone: 'success',
        message: 'Link copiado. Cole em "Adicionar por URL" no seu calendário.',
      });
    });

  const handleDownload = () =>
    run('download', async () => {
      await downloadCalendarIcs(targetContractId, { hideAmounts });
      setFeedback({
        tone: 'success',
        message:
          'Arquivo .ics baixado. Ele é um retrato de agora e não se atualiza sozinho — para sincronizar, use a assinatura.',
      });
    });

  return (
    <Backdrop open={open} onClose={onClose} className="max-w-[520px]">
      <div className="rounded-t-large bg-primary-01 px-medium py-small">
        <div className="flex items-start justify-between gap-smaller">
          <div>
            <Text className="text-paragraphLG color-intense-01">
              Vincular ao calendário
            </Text>
            <Text className="mt-quark text-captionLG color-intense-01/80">
              Os vencimentos entram na sua agenda com lembrete 3 dias antes e no
              dia.
            </Text>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-full bg-intense-01/15 hover:bg-intense-01/25"
            aria-label="Fechar"
          >
            <Icon name="icon-close" size={16} color="#FFFFFF" rounded={false} />
          </button>
        </div>
      </div>

      <div className="max-h-[70vh] overflow-y-auto px-medium py-small">
        {scopeItems.length > 1 ? (
          <div className="flex flex-col gap-quark">
            <Text className="text-paragraphMD color-complement-01">
              O que vincular
            </Text>
            {scope === 'contract' && contractLabel ? (
              <Text className="text-captionMD color-intense-06">
                {contractLabel}
              </Text>
            ) : null}
            <div className="mt-quark">
              <FilterChips
                items={scopeItems}
                active={scope}
                onChange={setScope}
              />
            </div>
          </div>
        ) : null}

        <div className="mt-medium flex flex-col gap-smaller">
          <Text className="text-paragraphMD color-complement-01">
            Onde vincular
          </Text>

          <TargetRow
            icon="icon-link"
            title="Google Agenda"
            description="Abre o Google Agenda para assinar. Atualiza sozinho quando uma parcela é baixada."
            loading={busy === 'google'}
            onClick={() => openInNewTab('google', (s) => s.urls.google)}
          />

          <TargetRow
            icon="icon-calendar"
            title="Outlook"
            description="Adiciona o calendário da internet na sua conta Outlook.com."
            loading={busy === 'outlook'}
            onClick={() => openInNewTab('outlook', (s) => s.urls.outlook)}
          />

          <TargetRow
            icon="icon-copy"
            title="Copiar link de assinatura"
            description="Para Apple Calendário, Thunderbird ou qualquer app que aceite URL."
            loading={busy === 'copy'}
            onClick={handleCopy}
          />

          <TargetRow
            icon="icon-download"
            title="Baixar arquivo .ics"
            description="Importação única, sem sincronização depois."
            loading={busy === 'download'}
            onClick={handleDownload}
          />
        </div>

        <label className="mt-medium flex cursor-pointer items-start gap-smaller rounded-medium border border-intense-03 bg-intense-02 p-small">
          <input
            type="checkbox"
            checked={hideAmounts}
            onChange={(e) => setHideAmounts(e.target.checked)}
            className="mt-[3px] h-[16px] w-[16px] accent-primary-01"
          />
          <span>
            <Text className="text-paragraphMD color-complement-01">
              Ocultar valores
            </Text>
            <Text className="mt-quark block text-captionMD color-intense-06">
              Mostra só a parcela e o banco, sem o valor. Use se o seu calendário
              é compartilhado.
            </Text>
          </span>
        </label>

        {feedback ? (
          <div
            className={cn(
              'mt-medium rounded-medium border p-small',
              feedback.tone === 'success'
                ? 'border-success-04 bg-success-01'
                : 'border-warning-02 bg-warning-01',
            )}
          >
            <Text
              className={cn(
                'text-bodyMD',
                feedback.tone === 'success'
                  ? 'color-success-06'
                  : 'color-warning-06',
              )}
            >
              {feedback.message}
            </Text>
          </div>
        ) : null}

        <Text className="mt-medium block text-captionMD color-intense-06">
          O link do calendário é pessoal: quem tiver o endereço vê seus
          vencimentos. Você pode revogá-lo a qualquer momento em Ajustes.
        </Text>
      </div>

      <div className="border-t border-intense-03 px-medium py-small">
        <Button text="Fechar" variant="ghost" onClick={onClose} fullWidth />
      </div>
    </Backdrop>
  );
}

function TargetRow({
  icon,
  title,
  description,
  loading,
  onClick,
}: {
  icon: propsIconsNames;
  title: string;
  description: string;
  loading?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="flex items-center gap-smaller rounded-medium border border-intense-03 bg-intense-01 p-small text-left hover:bg-intense-02 disabled:opacity-60"
    >
      <span className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[12px] bg-support-01">
        <Icon name={icon} size={18} color="#1D4733" rounded={false} />
      </span>
      <span className="flex-1">
        <Text className="text-paragraphMD color-complement-01">
          {loading ? 'Gerando...' : title}
        </Text>
        <Text className="mt-quark block text-captionMD color-intense-06">
          {description}
        </Text>
      </span>
      <Icon
        name="icon-chevron-right"
        size={16}
        color="#B0B0B0"
        rounded={false}
      />
    </button>
  );
}
